import { useEffect, useState } from 'react';
import flatten from 'lodash/flatten';

import { IFacetOption } from 'shared/types/ConstructorFacet';
import { SearchFilterGrid } from 'search/components/searchGrid/SearchFilterGrid';
import { SearchFilterSizeGenderSelectDesktop } from 'search/components/searchGrid/SearchFilterSizeGenderSelectDesktop';
import {
  SearchFilterDesktopTitle,
  SearchFilterDesktopCollapsible,
} from 'search/styles/SearchStyles';
import {
  sortGender,
  sortSizesByDisplayName,
  transformSizeFacet,
} from 'search/utils/sizeFilterUtils';
import { getConstructorCategoriesStrings } from 'search/enums/translations';

interface ISearchFilterSizeDesktop {
  facetType: string;
  filterOptions: Array<IFacetOption>;
  resultIds: Array<string>;
}

interface ISizeFilterProductType {
  facetType: string;
  productType: string;
  resultIds: Array<string>;
  sizeOptions: { [key: string]: Array<IFacetOption> };
}

const SizeFilterProductType = ({
  facetType,
  productType,
  resultIds,
  sizeOptions,
}: ISizeFilterProductType) => {
  const hasSizeSelected = flatten(Object.values(sizeOptions)).some(
    (option) => option.status === 'selected',
  );
  const [isExpanded, setIsExpanded] = useState(hasSizeSelected);
  const genders = Object.keys(sizeOptions).sort(sortGender);
  const [selectedGender, setSelectedGender] = useState(genders[0]);
  const options = sizeOptions[selectedGender]
    ?.sort(sortSizesByDisplayName)
    .filter((option) => option.display_name);

  useEffect(() => {
    setSelectedGender(genders[0]);
  }, [genders.join(',')]);

  const getHasFilterApplied = () => {
    let result = false;

    genders.forEach((gender) => {
      for (const size of sizeOptions[gender]) {
        if (size.status === 'selected') {
          result = true;
          return;
        }
      }
    });

    return result;
  };

  const toggleExpanded = () => setIsExpanded((prevState) => !prevState);

  const handleSelectChange = (value) => {
    setSelectedGender(value);
  };

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <>
      <SearchFilterDesktopTitle
        onClick={toggleExpanded}
        data-qa="size_filter_product_type"
        isFilterOpen={isExpanded}
        hasFilterApplied={getHasFilterApplied()}
      >
        {getConstructorCategoriesStrings(productType).toLowerCase()}
      </SearchFilterDesktopTitle>
      {isExpanded && (
        <SearchFilterDesktopCollapsible>
          <SearchFilterSizeGenderSelectDesktop
            options={genders}
            handleSelectChange={handleSelectChange}
            selectedGender={selectedGender}
          />
          <SearchFilterGrid filterOptions={options} facetType={facetType} resultIds={resultIds} />
        </SearchFilterDesktopCollapsible>
      )}
    </>
  );
};

export const SearchFilterSizeDesktop = ({
  filterOptions,
  facetType,
  resultIds,
}: ISearchFilterSizeDesktop) => {
  const transformedSizes = transformSizeFacet(filterOptions);

  return (
    <>
      {Object.keys(transformedSizes).map((productType) => (
        <SizeFilterProductType
          key={productType}
          productType={productType}
          facetType={facetType}
          sizeOptions={transformedSizes[productType]}
          resultIds={resultIds}
        />
      ))}
    </>
  );
};
